import { Helmet } from "react-helmet";
import { SideBar } from "../side-bar/SideBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleExclamation, faEdit, faEye, faPlus, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { ChangeEvent, useEffect, useState } from "react";
import { departmentPops } from "../../../utils/types";
import { 
    GET_DEPARTMENT_END_POINT, 
    UPDATE_DEPARTMENT_NAME_END_POINT, 
    DELETE_DEPARTMENT_END_POINT } 
from "../../../config/config";
import { api, getToken } from "../../../services/apis/auth-api/apiUrl";
import { Notification } from "../pop-ups/notification";
import { AxiosError } from "axios";
import Modal from "../modal/modal";
import AddDepartment from "../modal/department-modals/add-department";
import DepartmentMembers from "../modal/department-modals/departmentMember";

interface depDataProps {
    id: number,
    department_name : string,
    editing : boolean,
    deleting : boolean,
    loading : boolean
}

export default function ManageDepartments (){

    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalChildren, setModalChildren] = useState<React.JSX.Element | null>(null);
    const [departments, setDepartments] = useState<departmentPops[]>([]);
    const [depData, setDepData] = useState<depDataProps[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showNotif, setShowNotif] = useState<boolean>(false);
    const [notifValue, setNotifValue] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean | null>(null);

    useEffect(()=>{
        const getDepartments = async() => {
            try{
                const department_data = await api.get(GET_DEPARTMENT_END_POINT, {
                    headers : {'Authorization' : 'Bearer '+getToken()}
                })
                setDepartments(department_data.data);
                const data = department_data.data;
                setDepData(data.map((dep : departmentPops)=>({
                    ...dep,
                    editing : false,
                    deleting : false,
                    loading : false
                })));
                setIsLoading(false);
            }catch(e){}
        }
        getDepartments();
    },[showModal])

    const createModalContent = (content : React.JSX.Element) => {
        setModalChildren(content);
        setShowModal(true);
    }

    const setEditing = (id : number) => {
        const data = depData.map((dep : depDataProps)=>
            dep.id === id ? {...dep, editing : true} : dep
        )
        setDepData(data);
    }

    const handleUpdateDepName = (id : number, e : ChangeEvent<HTMLInputElement>) => {
        const data = departments.map((dep : departmentPops) => 
            dep.id === id ? {...dep, department_name : e.target.value} : dep)

        setDepartments(data);
    }

    const setDeleting = (id : number) => {
        const data = depData.map((dep : depDataProps)=>
            dep.id === id ? {...dep, deleting : true} : dep
        )
        setDepData(data);
    }

    const handleCancel = (id : number) => {
        const data = depData.map((dep : depDataProps)=>
            dep.id === id ? {...dep, editing : false, deleting : false} : dep
        )
        setDepData(data);
    }

    const handleSubmitRequest = async(method : string, id : number) => {
        let endPoint = method === 'put' ? UPDATE_DEPARTMENT_NAME_END_POINT : DELETE_DEPARTMENT_END_POINT;

        let updatedDepartmentName : string = '';

        for (const name of departments){
            if(name.id === id){
                updatedDepartmentName = name.department_name;
                break;
            }
        }

        if(updatedDepartmentName === '') {
            setNotifValue('Failed to get department name');
            setIsSuccess(false);
            setShowNotif(true)

            setTimeout(()=>{
                setIsSuccess(null)
                setShowNotif(false)
            },1000)   
            
            return false;
        }

        const data = depData.map((dep : depDataProps)=>
            dep.id === id ? {...dep, loading : true} : dep
        )
        setDepData(data)

        try{
            await api({
                method : method,
                url : endPoint+id,
                data : {name : updatedDepartmentName},
                headers : {'Authorization' : 'Bearer '+getToken()}
            })

            let updated_data : depDataProps[] = [];
            if(method === 'put'){
                updated_data = depData.map((dep : depDataProps)=>
                    dep.id === id ? {...dep, department_name : updatedDepartmentName, editing : false, deleting : false, loading : false} : dep
                )
            }else{
                updated_data = depData.filter((item : depDataProps)=>
                    item.id !== id
                 )
            }
            setDepData(updated_data)
            setNotifValue('Request successful.');
            setIsSuccess(true)
            setShowNotif(true)
        }catch(e){
            const error = (e as AxiosError);
            const data = depData.map((dep : depDataProps)=>
                dep.id === id ? {...dep, editing : false, deleting : false, loading : false} : dep
            )
            setDepData(data);
            if(error.response?.status === 401){
                setNotifValue('Session has expired.');
            }else{
                setNotifValue('Request unsuccessful.');
            }
            setIsSuccess(false);
        }
        setTimeout(()=>{
            setIsSuccess(null)
            setShowNotif(false)
        },3000)
    }

    return (
        <div className='content flex'>
             <Helmet>
                <title>Manage Departments</title>
            </Helmet>
            <SideBar />
            <div className='main-content'>
                
                <div className="bg-white w-full p-2">
                    <div className="text-gray-600">
                        Manage Department
                    </div>
                    <div className="mt-6 mb-2 flex items-center">
                        <div className="w-64 hidden">
                            <input type="text" className="text-box" placeholder="Search department...." />
                        </div>
                        <div className="ms-auto mx-4">
                            <button type="button" onClick={()=>createModalContent(<AddDepartment />)} className="button button-vcc-primary text-sm">
                                <FontAwesomeIcon icon={faPlus} />&nbsp;
                                Add Department
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-white w-full h-auto my-2">
                    <div className="table-container max-h-[34rem]">
                        <table className="w-full border bg-transparent">
                            <thead className="table-fixed-header border">
                                <tr>
                                    <th className="table-headers">#</th>
                                    <th className="table-headers">Department Name</th>
                                    <th className="table-headers">Action</th>
                                </tr>
                            </thead>
                            <tbody className="text-center">
                                {isLoading ? (
                                    <>
                                        <tr>
                                            <td className="table-body"><div className="table-loading"></div></td>
                                            <td className="table-body"><div className="table-loading"></div></td>
                                            <td className="table-body"><div className="table-loading"></div></td>
                                        </tr>
                                        <tr>
                                            <td className="table-body"><div className="table-loading"></div></td>
                                            <td className="table-body"><div className="table-loading"></div></td>
                                            <td className="table-body"><div className="table-loading"></div></td>
                                        </tr>
                                    </>
                                ) : (
                                    <>
                                        {departments.length > 0 ? (
                                            <>
                                                {depData.map((x, index)=>(
                                                    <tr className={x.deleting && !x.editing ? 'bg-red-200' : 'hover:bg-gray-300'}>
                                                        <td className="table-body">{index + 1}</td>
                                                        <td className="table-body px-4">
                                                            {x.editing && !x.deleting &&(
                                                                <>
                                                                    {departments.map((data) => data.id === x.id && (
                                                                        <><input type="text" className="text-box text-center" onChange={(e)=> handleUpdateDepName(x.id, e)} value={data.department_name} placeholder="Department name" /></>
                                                                    ))}
                                                                </>
                                                            )}
                                                            {!x.editing && x.deleting &&(
                                                                <>
                                                                    <i className="text-gray-500">
                                                                        <FontAwesomeIcon className="text-yellow-500" icon={faCircleExclamation} /> &nbsp;
                                                                        Delete department? Make sure to empty users from this department.
                                                                    </i>
                                                                </>
                                                            )}
                                                            {!x.editing && !x.deleting &&(
                                                                <>{x.department_name}</>
                                                            )}
                                                        </td>
                                                        <td className="table-body">
                                                            {x.editing || x.deleting ? (
                                                                <>
                                                                    {x.loading ? (
                                                                        <>
                                                                            <div className="text-center">
                                                                                <FontAwesomeIcon className="animate-spin text-xl" icon={faSpinner} />
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="flex gap-6 justify-center text-xl">
                                                                                <i onClick={()=>handleSubmitRequest(
                                                                                        x.editing && !x.deleting ? 'put' : 'delete', 
                                                                                        x.id
                                                                                    )}
                                                                                    className="text-green-500 cursor-pointer"
                                                                                >
                                                                                    <FontAwesomeIcon icon={faCheck} />
                                                                                </i>
                                                                                <i onClick={()=>handleCancel(x.id)} className="text-red-500 cursor-pointer">
                                                                                    <FontAwesomeIcon icon={faTimes} />
                                                                                </i>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="flex gap-6 justify-center text-lg">
                                                                        <i onClick={()=> setEditing(x.id)} className="text-blue-500 cursor-pointer">
                                                                            <FontAwesomeIcon icon={faEdit} />
                                                                        </i>
                                                                        <i onClick={()=> setDeleting(x.id)} className="text-red-500 cursor-pointer">
                                                                            <FontAwesomeIcon icon={faTrashAlt} />
                                                                        </i>
                                                                        <i onClick={()=>createModalContent(<DepartmentMembers id={x.id} dep_name={x.department_name} />)} className="text-gray-500 cursor-pointer">
                                                                            <FontAwesomeIcon icon={faEye} />
                                                                        </i>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <tr>
                                                    <td className="table-body" colSpan={3}>No Department found.</td>
                                                </tr>
                                            </>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
            {showModal && (
                <Modal onClose={()=>{setShowModal(false)}}> 
                    {modalChildren}
                </Modal>
             )}
            {showNotif && (
                <Notification notif={notifValue} success={isSuccess} />
             )}
        </div>
    );
}